import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Loader2, Minus, AlertTriangle, ShieldCheck, Radio } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { EVENT_STATUS } from "@/lib/format";

export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title ? `${title} — NEXUM LEGACY` : "NEXUM LEGACY — Alleanza competitiva Call of Duty";
    const m = document.querySelector('meta[name="description"]');
    if (m && description) m.setAttribute("content", description);
  }, [title, description]);
}

const STATUS_STYLE = {
  bozza: "border-[#C6C6CE]/20 text-[#C6C6CE]/70",
  iscrizioni_aperte: "border-emerald-400/30 text-emerald-300 bg-emerald-400/5",
  in_programma: "border-[#C6C6CE]/30 text-[#F5F5F7] bg-[#17171D]",
  in_corso: "border-[#962FFF]/50 text-[#d6b3ff] bg-[#962FFF]/15",
  concluso: "border-[#C6C6CE]/20 text-[#C6C6CE]",
  annullato: "border-red-400/30 text-red-300",
  archiviato: "border-[#C6C6CE]/10 text-[#C6C6CE]/50",
};

export function StatusBadge({ status, testId }) {
  return (
    <span data-testid={testId || "event-status-badge"} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.18em] font-semibold font-display ${STATUS_STYLE[status] || ""}`}>
      {status === "in_corso" && <span className="w-1.5 h-1.5 rounded-full bg-[#962FFF] live-dot" />}
      {EVENT_STATUS[status] || status}
    </span>
  );
}

export function OfficialBadge({ official }) {
  return official ? (
    <span data-testid="official-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#962FFF]/40 bg-[#962FFF]/15 text-[#d6b3ff] text-[10px] uppercase tracking-[0.18em] font-semibold font-display">
      <ShieldCheck className="w-3.5 h-3.5" /> Ufficiale
    </span>
  ) : (
    <span data-testid="provisional-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-400/30 bg-amber-400/5 text-amber-200 text-[10px] uppercase tracking-[0.18em] font-semibold font-display">
      <Radio className="w-3.5 h-3.5" /> Provvisoria
    </span>
  );
}

export function PosChange({ value }) {
  if (!value) return <Minus className="w-3.5 h-3.5 text-[#C6C6CE]/40" aria-label="Invariata" />;
  const up = value > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 font-num text-xs ${up ? "text-emerald-400" : "text-red-400"}`} aria-label={up ? `Salita di ${value}` : `Scesa di ${-value}`}>
      <Icon className="w-3.5 h-3.5" />{Math.abs(value)}
    </span>
  );
}

export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <div className="relative border-b border-[#C6C6CE]/10 overflow-hidden">
      <div className="absolute -top-40 left-1/4 w-[700px] h-[400px] purple-glow pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-28 pb-12 md:pt-36 md:pb-16 fade-up">
        {eyebrow && <div className="eyebrow mb-4">{eyebrow}</div>}
        <h1 data-testid="page-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight silver-text">{title}</h1>
        {subtitle && <p className="mt-4 max-w-2xl text-sm md:text-base text-[#C6C6CE]/80">{subtitle}</p>}
        {children && <div className="mt-8">{children}</div>}
      </div>
    </div>
  );
}

export function SectionTitle({ eyebrow, title, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-8">
      <div>
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-[#F5F5F7]">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, testId = "empty-state", children }) {
  return (
    <div data-testid={testId} className="nx-card border-dashed px-6 py-14 text-center">
      {Icon && <Icon className="w-8 h-8 mx-auto text-[#962FFF]/70 mb-4" strokeWidth={1.5} />}
      <div className="font-display uppercase tracking-wider text-[#F5F5F7]">{title}</div>
      {text && <p className="mt-2 text-sm text-[#C6C6CE]/60 max-w-md mx-auto">{text}</p>}
      {children && <div className="mt-6">{children}</div>}
    </div>
  );
}

export function Loader({ label = "Caricamento..." }) {
  return (
    <div data-testid="loader" className="flex items-center justify-center gap-3 py-16 text-[#C6C6CE]/70 text-sm">
      <Loader2 className="w-5 h-5 animate-spin text-[#962FFF]" /> {label}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div data-testid="error-state" className="nx-card border-red-500/20 px-6 py-10 text-center">
      <AlertTriangle className="w-7 h-7 mx-auto text-red-400 mb-3" />
      <p className="text-sm text-[#C6C6CE]">{message}</p>
      {onRetry && <button data-testid="retry-button" onClick={onRetry} className="btn-ghost btn-sm mt-5">Riprova</button>}
    </div>
  );
}

export function Pagination({ page, pages, onChange }) {
  if (!pages || pages <= 1) return null;
  return (
    <div data-testid="pagination" className="flex items-center justify-center gap-3 mt-10">
      <button data-testid="pagination-prev" disabled={page <= 1} onClick={() => onChange(page - 1)} className="btn-ghost btn-sm" aria-label="Pagina precedente"><ChevronLeft className="w-4 h-4" /></button>
      <span className="font-num text-sm text-[#C6C6CE]">{page} / {pages}</span>
      <button data-testid="pagination-next" disabled={page >= pages} onClick={() => onChange(page + 1)} className="btn-ghost btn-sm" aria-label="Pagina successiva"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}

export function Confirm({ trigger, title, description, onConfirm, confirmLabel = "Conferma", testId = "confirm-dialog" }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent data-testid={testId} className="bg-[#0B0B0F] border-[#C6C6CE]/15">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display uppercase tracking-wide">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[#C6C6CE]/80">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`} className="bg-transparent border-[#C6C6CE]/20">Annulla</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testId}-confirm`} onClick={onConfirm} className="bg-[#962FFF] hover:bg-[#a54dff]">{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="nx-label">{label}</span>
      {children}
    </label>
  );
}

export function BackLink({ to, label, testId = "back-link" }) {
  return (
    <Link data-testid={testId} to={to} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#C6C6CE]/70 hover:text-[#F5F5F7] transition-colors duration-200">
      <ChevronLeft className="w-4 h-4" /> {label}
    </Link>
  );
}
